import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageForm from '../../../../templates/PageForm';
import StudioSkeleton from './StudioSkeleton';
import Dialog from '../../../../organisms/Dialog';
import StatusReasonModal from '../../StatusReasonModal';
import CompositionDeactivateModal from '../CompositionDeactivateModal';
import ChannelPicker from './ChannelPicker';
import StudioNav, { STUDIO_SECTIONS } from './StudioNav';
import StudioStatusBar from './StudioStatusBar';
import StudioToolbar from './StudioToolbar';
import ContentSection from './sections/ContentSection';
import DeliverySection from './sections/DeliverySection';
import IdentitySection from './sections/IdentitySection';
import ReviewSection from './sections/ReviewSection';
import VariablesSection from './sections/VariablesSection';
import PreviewPanel from './preview/PreviewPanel';
import { StudioMode, useMessageCompositionStudio } from './useMessageCompositionStudio';
import { isEnvelope } from './studioDraft';
import { Group } from '../../../../../types/communication';

const LIST_URL = '/communication/message-compositions/0/20/name/ASC';

const SECTION_KEYS = STUDIO_SECTIONS.map((s) => s.key);
const isValidSection = (value: string | undefined): value is (typeof SECTION_KEYS)[number] =>
    !!value && (SECTION_KEYS as string[]).includes(value);

const MessageCompositionStudioPage: React.FC<{ mode: StudioMode }> = ({ mode }) => {
    const navigate = useNavigate();
    const { id, section: sectionParam } = useParams();
    const studio = useMessageCompositionStudio(id, mode);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [toggling, setToggling] = useState(false);
    const { meta, section } = studio;
    const [searchParams] = useSearchParams();
    const requestedTemplateId = searchParams.get('template');
    const requested = studio.templates.find((t) => t.id === requestedTemplateId);
    const requestedId = requested?.id;
    const requestedSection = requested && isEnvelope(requested) ? 'delivery' : 'content';
    const { setActiveTemplateId, setSection } = studio;

    const sectionBaseUrl = id
        ? mode === 'edit'
            ? `/communication/message-composition/${id}/edit`
            : `/communication/message-composition/${id}`
        : null;

    const goToSection = (next: (typeof SECTION_KEYS)[number]) => {
        setSection(next);
        if (sectionBaseUrl) navigate(`${sectionBaseUrl}/${next}`);
    };

    const currentSectionUrl = sectionBaseUrl
        ? `${sectionBaseUrl}/${section}`
        : mode === 'new'
            ? '/communication/message-composition/new'
            : LIST_URL;
    const groupOutletContext = {
        closeTo: currentSectionUrl,
        onGroupCreated: (group: Group) => {
            studio.addGroup(group);
            studio.updateMeta({ groupId: group.id });
        },
    };

    useEffect(() => {
        if (mode === 'new') return;
        if (isValidSection(sectionParam)) {
            if (sectionParam !== section) setSection(sectionParam);
        } else if (sectionBaseUrl) {
            navigate(`${sectionBaseUrl}/${section}`, { replace: true });
        }
    }, [sectionParam, section, mode, sectionBaseUrl, setSection, navigate]);

    useEffect(() => {
        if (!requestedId) return;
        setActiveTemplateId(requestedId);
        setSection(requestedSection);
    }, [requestedId, requestedSection, setActiveTemplateId, setSection]);

    const save = async () => {
        const saved = await studio.save();
        if (saved) navigate(`/communication/message-composition/${saved.id}`);
    };

    const cancel = () => {
        if (mode === 'edit' && meta) {
            studio.discard();
            navigate(`/communication/message-composition/${meta.id}`);
        } else {
            navigate(LIST_URL);
        }
    };

    const duplicate = async () => {
        const clone = await studio.duplicateMeta();
        if (clone) navigate(`/communication/message-composition/${clone.id}/edit`);
    };

    const sectionView = () => {
        switch (section) {
            case 'identity':
                return <IdentitySection studio={studio} />;
            case 'delivery':
                return <DeliverySection studio={studio} />;
            case 'variables':
                return <VariablesSection studio={studio} />;
            case 'review':
                return <ReviewSection studio={studio} />;
            default:
                return <ContentSection studio={studio} />;
        }
    };

    const goToList = () => navigate(LIST_URL);

    const serialLabel = meta?.serialId ? `Composição #${meta.serialId}` : 'Composição';
    const title = mode === 'new' ? 'Nova composição' : mode === 'edit' ? 'Edição' : serialLabel;

    const breadcrumb =
        mode === 'new'
            ? [
                  { label: 'Composições', url: '#', onClick: goToList },
                  { label: 'Nova', url: '#' },
              ]
            : mode === 'edit'
                ? [
                      { label: 'Composições', url: '#', onClick: goToList },
                      {
                          label: serialLabel,
                          url: '#',
                          onClick: meta ? () => navigate(`/communication/message-composition/${meta.id}`) : undefined,
                      },
                      { label: 'Edição', url: '#' },
                  ]
                : [
                      { label: 'Composições', url: '#', onClick: goToList },
                      { label: serialLabel, url: '#' },
                  ];

    return (
        <PageForm
            pageTitle={title}
            truncateTitle
            apiVersion={studio.apiVersion}
            isLoading={studio.saving}
            breadcrumb={breadcrumb}
            outletContext={groupOutletContext}
            controlSlot={
                <StudioToolbar
                    studio={studio}
                    mode={mode}
                    onSave={save}
                    onCancel={cancel}
                    onDuplicate={duplicate}
                    onToggleStatus={() => setToggling(true)}
                    onOpenPreview={() => setPreviewOpen(true)}
                />
            }
        >
            {studio.loading && (
                <div className="py-2">
                    <StudioSkeleton mode={mode} />
                </div>
            )}
            {!studio.loading && !meta && mode === 'new' && <ChannelPicker onStart={studio.start} />}
            {!studio.loading && meta && <StudioStatusBar studio={studio} mode={mode} />}
            {!studio.loading && meta && (
                <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[220px_minmax(0,1fr)_400px] gap-5 py-4 lg:h-[calc(100%-2.5rem)]">
                    <aside className="lg:overflow-y-auto">
                        <StudioNav
                            current={section}
                            issues={mode === 'view' ? [] : studio.issues}
                            onSelect={goToSection}
                            variableCount={studio.activeTemplate?.variables.length ?? 0}
                        />
                    </aside>
                    <main className="min-w-0 lg:overflow-y-auto pb-10 pr-1">{sectionView()}</main>
                    <aside className="hidden xl:block overflow-y-auto pb-10 pr-1">
                        <PreviewPanel studio={studio} />
                    </aside>
                </div>
            )}
            <Dialog
                id="composition-preview"
                show={previewOpen}
                onClose={() => setPreviewOpen(false)}
                header="Pré-visualização"
                size="lg"
                body={<PreviewPanel studio={studio} />}
            />
            <CompositionDeactivateModal
                isOpen={toggling && meta?.status === 'ACTIVE'}
                compositionLabel={meta?.serialId ? `Composição #${meta.serialId}` : 'composição'}
                onCancel={() => setToggling(false)}
                onConfirm={async (reason) => {
                    setToggling(false);
                    await studio.toggleStatus(reason);
                }}
            />
            <StatusReasonModal
                isOpen={toggling && meta?.status !== 'ACTIVE'}
                title="Ativar composição"
                actionLabel="Ativar"
                onCancel={() => setToggling(false)}
                onConfirm={async (reason) => {
                    setToggling(false);
                    await studio.toggleStatus(reason);
                }}
            />
        </PageForm>
    );
};

export default MessageCompositionStudioPage;
